import type { PersistentChatMessageData } from "~shared/types/persistent-chat-types";

type QuestionRestoreMessage = Pick<PersistentChatMessageData, "id" | "role" | "metadata">;

function isAssistantQuestionMessage(message: QuestionRestoreMessage | undefined): boolean {
  if (!message || message.role !== "assistant") return false;
  return Boolean(message.metadata?.questionAsked && message.metadata?.userQuestion);
}

/** True when the thread has moved past this question (answer sent and/or a later assistant reply). */
export function isQuestionMessageResolvedInHistory(
  messages: readonly QuestionRestoreMessage[],
  questionIndex: number,
): boolean {
  for (let i = questionIndex + 1; i < messages.length; i += 1) {
    const message = messages[i];
    if (message.role === "user" && message.metadata?.questionAnswer === true) {
      return true;
    }
    if (message.role === "assistant" && !message.metadata?.hiddenFromUI) {
      return true;
    }
    if (message.role === "user" && !message.metadata?.hiddenFromUI) {
      return true;
    }
  }
  return false;
}

/** Assistant question message ids that already have a user answer or a later assistant turn in history. */
export function collectAnsweredQuestionMessageIdsFromHistory(
  messages: readonly QuestionRestoreMessage[],
): string[] {
  const answered: string[] = [];
  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i];
    if (!isAssistantQuestionMessage(message) || !message.id) continue;
    if (isQuestionMessageResolvedInHistory(messages, i)) {
      answered.push(message.id);
    }
  }
  return answered;
}

/**
 * Returns the assistant question row to restore, only if it is still the open prompt on the thread.
 * Does not fall back to older question rows when a later assistant message has already continued the chat.
 */
export function resolvePendingQuestionMessageToRestore(
  messages: readonly QuestionRestoreMessage[],
): QuestionRestoreMessage | undefined {
  if (!messages.length) return undefined;

  const lastMessage = messages[messages.length - 1];
  if (isAssistantQuestionMessage(lastMessage)) {
    return lastMessage;
  }

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!isAssistantQuestionMessage(message)) continue;
    if (isQuestionMessageResolvedInHistory(messages, i)) continue;
    return message;
  }

  return undefined;
}

/**
 * When the thread tail is a user message but the live question is only in store (not yet on an
 * assistant row), keep the overlay — e.g. CTA turns persisted before ask_user_question lands.
 */
export function shouldKeepLiveQuestionWithUserMessageTail(
  messages: readonly QuestionRestoreMessage[],
  options?: { ctaCancelled?: boolean; hasStreamError?: boolean; currentTurnUserMessageId?: string | null },
): boolean {
  if (options?.ctaCancelled || options?.hasStreamError) return false;
  const tail = messages[messages.length - 1];
  // Earlier assistants belong to earlier turns. A newly streamed question must survive
  // completion while this turn's assistant row is still missing from the cache.
  if (tail?.role === "user" && tail.id === options?.currentTurnUserMessageId) return true;
  return !messages.some((message) => message.role === "assistant");
}
